import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { customFieldsService } from '../api/custom-fields.service';
import { CUSTOM_FIELD_KEYS } from './queryKeys';

/**
 * Fetches the custom field values assigned to a specific task.
 *
 * @param taskId - The task whose field values to fetch.
 * @returns TanStack Query result with `data` as `FieldValue[]`.
 *
 * @remarks
 * - Disabled until `activeOrganizationId` is set, or when `enabled: false` is
 *   passed — e.g. a list-view cell that already has its value from
 *   `useTaskFieldValuesBatch` has no need for its own per-task fetch.
 * - `staleTime` is 2 minutes — shorter than field definitions because values change more frequently.
 * - Invalidated when `useUpsertTaskFieldValue` succeeds.
 */
export function useTaskFieldValues(taskId: string, options: { enabled?: boolean } = {}) {
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);
  const { enabled = true } = options;

  return useQuery({
    queryKey: CUSTOM_FIELD_KEYS.taskValues(activeOrganizationId ?? '', taskId),
    queryFn: ({ signal }) => customFieldsService.getTaskFieldValues(taskId, signal),
    staleTime: 120_000,
    enabled: enabled && !!activeOrganizationId && !!taskId,
  });
}
